import { QueryClient } from "@tanstack/react-query";
import { TRPCClientError, createTRPCClient, httpBatchLink } from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import superjson from "superjson";
import type { AppRouter } from "@/server/api/root";

// Single shared client. Consumed directly by some route loaders, and wrapped by the
// options-proxy below for anything going through TanStack Query.
export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch: (input, init) => fetch(input, { ...init, credentials: "include" }),
    }),
  ],
});

// The one QueryClient for the app. Created here (not in main.tsx) so this same instance
// backs the options-proxy below, the <QueryClientProvider> in main.tsx, and the router
// context (src/router-context.ts) — loaders, components, and the proxy all share one cache.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      // A tRPC error carrying a 4xx status (UNAUTHORIZED, FORBIDDEN, NOT_FOUND,
      // BAD_REQUEST…) is deterministic — retrying can't fix it, it only delays
      // the redirect/403 that route loaders and errorComponents render from it
      // (the old direct-trpcClient loaders failed fast). Keep the default three
      // retry for genuinely transient failures (network errors, 5xx), but only
      // once: three retries made a failed server look like a very slow page.
      retry: (failureCount, error) => {
        if (
          error instanceof TRPCClientError &&
          typeof (error.data as { httpStatus?: unknown } | undefined)?.httpStatus === "number" &&
          (error.data as { httpStatus: number }).httpStatus < 500
        ) {
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});

// Options-proxy (https://trpc.io/docs/client/tanstack-react-query/setup — "without React
// context" pattern). Lets both loaders and components build query/mutation options against the
// shared queryClient: `queryClient.ensureQueryData(trpc.x.y.queryOptions(input))` in a loader,
// `useQuery(trpc.x.y.queryOptions(input))` / `useMutation(trpc.x.y.mutationOptions())` in a
// component — both hit the same cache entry.
export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});
